import React, { useMemo } from 'react';
import { BodyText, FlexRow, Grid } from 'ui-toolkit';
import { IStandardDocumentVersionData } from '../../../../types/order/documents/shared/interfaces';
import { FinalizePolicesController } from '../FinalizePolicesController';
import { PurchaseTitlePolicy } from '../../../../components/order/title/titlePolicy/PurchaseTitlePolicy';
import { LoanPolicy } from '../../../../components/order/title/titlePolicy/LoanPolicy';
import { useSelectCurrentOrder } from '../../../../selectors/order/selectCurrentOrder';
import { isAmericanDigital } from '../../../../utilities/order/hasAmericanDigitalUnderwriter';
import {
  buildGetCategoryLabelByName,
  getKeyCategoryLabel,
  HomeownersPolicyName,
  LongFormLendersPolicyNames,
  OwnersPolicyNames,
  ShortFormLendersPolicyNames,
} from '../../../../types/order/documents/IDocumentCategoryType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../API/documentCategories/DocumentCategoriesApi';
import { ErrorMessageTW } from '../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { LoadingSpinnerTW } from '../../../../tailwindComponents/loadingSpinnerTW/LoadingSpinnerTW';

export interface ITitlePolicyPageProps {
  isCommitmentGenerated: boolean;
  ownerPolicy?: IStandardDocumentVersionData;
  longFormPolicy?: IStandardDocumentVersionData;
  shortFormPolicy?: IStandardDocumentVersionData;
}

export const TitlePolicyPage: React.FC<ITitlePolicyPageProps> = ({
  isCommitmentGenerated,
  ownerPolicy,
  longFormPolicy,
  shortFormPolicy,
}) => {
  const { data } = useSelectCurrentOrder();
  const {
    data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData,
    isLoading,
    isSuccess,
    error,
  } = useListDocumentCategoriesQuery();

  const [ownerLabel, longLabel, shortLabel] = useMemo(() => {
    if (!isSuccess) {
      return ['', '', ''];
    }
    const ownerPolicyCategory = data.property.state === 'OH' ? HomeownersPolicyName : OwnersPolicyNames.currentName;
    return [ownerPolicyCategory, LongFormLendersPolicyNames.currentName, ShortFormLendersPolicyNames.currentName].map(
      (categoryName: string) => {
        try {
          return getKeyCategoryLabel(categoryName, documentCategoriesByName);
        } catch (e) {
          return '';
        }
      }
    );
  }, [data.property.state, documentCategoriesByName, isSuccess]);

  if (!isCommitmentGenerated) {
    return <BodyText>Policy creation will be available after the commitment has been generated.</BodyText>;
  }

  const isAdTic = isAmericanDigital(data);

  const getCategoryLabel = buildGetCategoryLabelByName(documentCategoriesByName);

  return (
    <LoadingSpinnerTW isLoading={isLoading}>
      <Grid>
        <ErrorMessageTW rtkError={error} />
        {isAdTic && (
          <FlexRow justifyContent={'flex-end'}>
            <FinalizePolicesController getCategoryLabel={getCategoryLabel} />
          </FlexRow>
        )}
        {isSuccess && (
          <>
            <PurchaseTitlePolicy isAmericanDigital={isAdTic} ownerPolicy={ownerPolicy} label={ownerLabel} />
            <LoanPolicy
              isAmericanDigital={isAdTic}
              longFormPolicy={longFormPolicy}
              shortFormPolicy={shortFormPolicy}
              longLabel={longLabel}
              shortLabel={shortLabel}
            />
          </>
        )}
      </Grid>
    </LoadingSpinnerTW>
  );
};
